import { OneOnOneAgenda, OneOnOneCreatedAgenda } from 'screens/oneOnOneRabbit/interfaces/OneOnOneAgenda';
import { OneOnOneRequestI } from 'screens/oneOnOneRabbit/interfaces/common';
import { z } from 'zod';

import {
  createOneOnOneAgendaApi,
  deleteOneOnOneAgendaApi,
  deleteOneOnOneAiRecommendationsApi,
  getOneOnOneAgendaApi,
  getOneOnOneBotRecommendationsApi,
  updateOneOnOneAgendaApi,
  updateOneOnOneAgendaCompletionApi,
  updateOneOnOneAgendaNotesApi,
} from '../api';
import { OneOnOneAgendaNotesI, ReorderAgendaI } from '../hooks/dataHooks';

export const oneOnOneAgendaValidator = z.array(OneOnOneAgenda.nullable()).nullable();

export const getOneOnOneAgenda = async ({ masterId, instanceId }: OneOnOneRequestI) => {
  try {
    const res = await getOneOnOneAgendaApi({ masterId, instanceId });
    const data = oneOnOneAgendaValidator.parse(res.entity);
    const adminAddedAgenda = data.filter((agenda) => agenda?.isOwnerAdmin);
    const userAddedAgenda = data
      .filter((agenda) => !agenda?.isOwnerAdmin)
      .sort((a, b) => a.displayOrder - b.displayOrder);
    return [...adminAddedAgenda, ...userAddedAgenda];
  } catch (error) {
    console.error('Error fetching one on one agenda details ', error);
    // throw new Error(generateErrorMessage(error));
  }
};

type OneOnOneAgendaCreateI = OneOnOneRequestI & z.infer<typeof OneOnOneAgenda>;

export const createOneOnOneAgenda = async ({ masterId, instanceId, ...rest }: OneOnOneAgendaCreateI) => {
  try {
    const res = await createOneOnOneAgendaApi({ masterId, instanceId, ...rest });
    const data = OneOnOneCreatedAgenda.parse(res.entity);
    return data;
  } catch (error) {
    console.error('Error creating one on one agenda ', error);
    // throw new Error(generateErrorMessage(error));
  }
};

// REFER: https://stackoverflow.com/a/49725198/8487836
export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
  }[Keys];

type OneOnOneAgendaRequiredI = RequireAtLeastOne<OneOnOneAgendaCreateI, 'instanceDataId' | 'templateAgendaId'>;

export type OneOnOneAgendaUpdateI = OneOnOneRequestI & OneOnOneAgendaRequiredI & z.infer<typeof OneOnOneAgenda>;

export const updateOneOnOneAgenda = async ({
  masterId,
  instanceId,
  instanceDataId,
  templateAgendaId,
  ...rest
}: OneOnOneAgendaUpdateI) => {
  try {
    await updateOneOnOneAgendaApi({
      masterId,
      instanceId,
      instanceDataId,
      templateAgendaId,
      ...rest,
    });
    // const data = oneOnOneAgendaValidator.parse(res.entity);
    // return data;
  } catch (error) {
    console.error('Error update one on one agenda ', error);
    // throw new Error(generateErrorMessage(error));
  }
};

export const deleteOneOnOneAgenda = async ({
  masterId,
  instanceId,
  instanceDataId,
}: OneOnOneRequestI & { instanceDataId: number }) => {
  try {
    await deleteOneOnOneAgendaApi({
      masterId,
      instanceId,
      instanceDataId,
    });
    // const data = oneOnOneAgendaValidator.parse(res.entity);
    // return data;
  } catch (error) {
    console.error('Error deleting one on one agenda ', error);
    // throw new Error(generateErrorMessage(error));
  }
};

type UpdateAgendaCompletionI = {
  isCompleted: boolean;
  instanceDataId?: number;
  templateAgendaId?: number;
};

type OneOnOneAgendaCompletionI = OneOnOneRequestI &
  RequireAtLeastOne<UpdateAgendaCompletionI, 'instanceDataId' | 'templateAgendaId'>;

export const updateOneOnOneAgendaCompletion = async ({
  masterId,
  instanceId,
  instanceDataId,
  templateAgendaId,
  isCompleted,
}: OneOnOneAgendaCompletionI) => {
  try {
    await updateOneOnOneAgendaCompletionApi({
      masterId,
      instanceId,
      instanceDataId,
      templateAgendaId,
      isCompleted,
    });
    // const data = oneOnOneAgendaValidator.parse(res.entity);
    // return data;
  } catch (error) {
    console.error('Error update one on one agenda ', error);
    // throw new Error(generateErrorMessage(error));
  }
};

export type OneOnOneAgendaNotesUpdateI = OneOnOneRequestI &
  RequireAtLeastOne<
    {
      instanceDataId?: number;
      templateAgendaId?: number;
    },
    'instanceDataId' | 'templateAgendaId'
  > &
  OneOnOneAgendaNotesI;

export const updateOneOnOneAgendaNotes = async ({
  masterId,
  instanceId,
  instanceDataId,
  templateAgendaId,
  ...rest
}: OneOnOneAgendaNotesUpdateI) => {
  try {
    await updateOneOnOneAgendaNotesApi({
      masterId,
      instanceId,
      instanceDataId,
      templateAgendaId,
      ...rest,
    });
    // const data = oneOnOneAgendaValidator.parse(res.entity);
    // return data;
  } catch (error) {
    console.error('Error update one on one agenda ', error);
    // throw new Error(generateErrorMessage(error));
  }
};

export const reorderOneOnOneAgenda = async ({
  masterId,
  instanceId,
  reorderInstanceDataId,
  topInstanceDataId,
}: OneOnOneRequestI & ReorderAgendaI) => {
  try {
    await reorderOneOnOneAgenda({
      masterId,
      instanceId,
      reorderInstanceDataId,
      topInstanceDataId,
    });
    // const data = oneOnOneAgendaValidator.parse(res.entity);
    // return data;
  } catch (error) {
    console.error('Error reordering one on one agenda ', error);
    // throw new Error(generateErrorMessage(error));
  }
};

export const getOneOnOneBotRecommendedData = async ({ masterId, instanceId }: OneOnOneRequestI) => {
  try {
    const res = await getOneOnOneBotRecommendationsApi({ masterId, instanceId });
    const data = res.entity;
    return data;
  } catch (error) {
    console.error('Error fetching one on one bot recommended data ', error);
    // throw new Error(generateErrorMessage(error));
  }
};

export const deleteOneOnOneAiRecommendedData = async ({
  masterId,
  instanceId,
  aiGeneratedDataId,
}: OneOnOneRequestI) => {
  try {
    const res = await deleteOneOnOneAiRecommendationsApi({
      masterId,
      instanceId,
      aiGeneratedDataId,
    });
    const data = res.entity;
    return data;
  } catch (error) {
    console.error('Error deleting one on one ai recommended data ', error);
    // throw new Error(generateErrorMessage(error));
  }
};
